/**
 * Ícone do grupo de produto, exibido na navegação do cardápio público.
 *
 * Os valores são **ids de símbolos do sprite `material-outline.svg`** que o app já serve em
 * `apps/frontend/public/icons/` — nada de arquivo novo nem de biblioteca de ícones extra.
 *
 * Por que um enum e não uma string livre: este valor vai para o payload público e vira
 * `<use href="icons/material-outline.svg#{icon}">` no cardápio. String livre vinda do cadastro
 * seria conteúdo controlado pelo usuário desenhando referência de recurso — o enum fecha isso
 * com `@IsEnum` no DTO, e ainda garante que o id existe de fato no sprite.
 *
 * Como as duas telas consomem:
 * - **Admin** (tem Material): `<mat-icon svgIcon="mat_outline:{icon}">`
 * - **Cardápio público** (não importa Material, de propósito — ver D19 e a poda de bundle):
 *   `<svg><use href="icons/menu-sprite.svg#{icon}"></use></svg>` — o sprite MÍNIMO gerado por
 *   `npm run generate:menu-sprite`, que inclui TODOS os valores deste enum; ícone novo aqui
 *   exige rodar o script de novo (o `sprite-icons.spec.ts` acusa se esquecer).
 */
export enum MenuGroupIcon {
  COFFEE = 'local_cafe',
  TEA = 'emoji_food_beverage',
  JUICE = 'local_bar',
  BEER = 'sports_bar',
  WINE = 'wine_bar',
  SPIRITS = 'liquor',
  BREAKFAST = 'brunch_dining',
  BAKERY = 'bakery_dining',
  BURGER = 'lunch_dining',
  PIZZA = 'local_pizza',
  SNACKS = 'tapas',
  FAST_FOOD = 'fastfood',
  NOODLES = 'ramen_dining',
  MAIN_DISH = 'restaurant',
  SEAFOOD = 'set_meal',
  DESSERT = 'cake',
  ICE_CREAM = 'icecream',
}

/** Lista estável para montar o seletor no cadastro de grupo. */
export const MENU_GROUP_ICONS: MenuGroupIcon[] = Object.values(MenuGroupIcon);

/** Ícone usado quando o grupo não tem um escolhido, por categoria do cardápio. */
export const DEFAULT_MENU_GROUP_ICON: Record<'FOOD' | 'DRINK' | 'OTHER', MenuGroupIcon> = {
  FOOD: MenuGroupIcon.MAIN_DISH,
  DRINK: MenuGroupIcon.JUICE,
  OTHER: MenuGroupIcon.MAIN_DISH,
};

/**
 * 🔴 **As CHAVES do enum — é isto que o contrato de integração publica (F14/D198), nunca o valor.**
 *
 * O parceiro manda `"BEER"`; o id do sprite (`sports_bar`) é implementação nossa. Publicar o valor
 * amarraria o integrador ao sprite: trocar de conjunto de ícones quebraria todo mundo de uma vez, e
 * um `"local_cafe"` gravado no PMS do cliente não teria como ser migrado.
 */
export type MenuGroupIconKey = keyof typeof MenuGroupIcon;

/** Lista estável das chaves, na ordem de declaração — é a tabela publicada no contrato. */
export const MENU_GROUP_ICON_KEYS = Object.keys(MenuGroupIcon) as MenuGroupIconKey[];

/**
 * Chave do contrato → id do sprite, que é o que se grava.
 *
 * ⚠️ **Preserva `undefined` e `null`**, e a distinção é o contrato inteiro: `undefined` é "não
 * informei" (o Prisma descarta e o valor gravado fica), `null` é "apague" (só o `PATCH` aceita).
 * Colapsar os dois num `?? null` faria todo full-sync sem o campo limpar o ícone do gerente.
 *
 * Chave desconhecida vira `null` por segurança, mas não é o caminho normal: o DTO a recusa com `400`
 * antes de chegar aqui.
 */
export function menuGroupIconFromKey(key: string | null | undefined): MenuGroupIcon | null | undefined {
  // Os dois `return` são literais, e não `return key`: este projeto compila com `strictNullChecks`
  // desligado, então o compilador não estreita `string | null | undefined` para os dois casos aqui.
  if (key === undefined) {
    return undefined;
  }
  if (key === null) {
    return null;
  }

  // 🔴 A checagem é contra a LISTA de chaves, não `MenuGroupIcon[key] ?? null`: enum de TS compila
  // para objeto literal, com `Object.prototype` na cadeia — `menuGroupIconFromKey('constructor')`
  // devolveria uma FUNÇÃO, que o Prisma recusaria com erro cru (`500` na superfície do parceiro).
  // Hoje o DTO já barra isso com `400`; esta linha é o backstop para o próximo chamador (importador,
  // backfill, um lote de grupos), que pode não ter DTO na frente.
  return MENU_GROUP_ICON_KEYS.includes(key as MenuGroupIconKey) ? MenuGroupIcon[key as MenuGroupIconKey] : null;
}

/**
 * Id do sprite gravado → chave do contrato. É a metade que fecha o **round-trip**: sem ela o parceiro
 * leria `local_cafe` no `GET`, reenviaria no upsert seguinte e tomaria `400` num full-sync que só
 * repetiu o que nós demos a ele.
 *
 * Valor que não casa nenhuma chave (legado, ou ícone removido do enum) sai como `null` — fail-safe.
 * O grupo continua aparecendo; o que o parceiro perde é a informação do ícone, não o item.
 */
export function menuGroupIconToKey(icon: string | null | undefined): MenuGroupIconKey | null {
  if (!icon) {
    return null;
  }

  return MENU_GROUP_ICON_KEYS.find((key) => MenuGroupIcon[key] === icon) ?? null;
}
